import { useCallback, useReducer } from "react";
import imageCompression from "browser-image-compression";
import { UploadState, ProfileImageUrls } from "./upload.types";
import { useSnackbar } from "@mindlercare/ui-web";
import { useTranslation } from "react-i18next";
import { httpClient } from "../../../services/httpClient";

const initialUploadState: UploadState = {
  file: null,

  uploading: false,
  uploadProgress: null,
  successfulUploaded: false,
  uploadingMessage: null,

  uploaded: {
    url: null,
    thumbnailUrl: null,
  },
};

export const useUpload = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useReducer(
    (prev: UploadState, next: Partial<UploadState>) => ({ ...prev, ...next }),
    initialUploadState,
  );

  const setFile = useCallback((file?: File) => {
    setState({
      file,
      successfulUploaded: false,
      uploadingMessage: null,
    });
  }, []);

  const sendRequest = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("image", file, file.name);
      try {
        const res = await httpClient.post(
          "statics/images/upload/profile_pictures",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (event) => {
              if (event.total) {
                const copy = { ...state.uploadProgress };
                copy[file.name] = {
                  state: "pending",
                  percentage: (event.loaded / event.total) * 100,
                };
                setState({ uploadProgress: copy });
              }
            },
          },
        );
        const successCopy = { ...state.uploadProgress };
        successCopy[file.name] = { state: "done", percentage: 100 };
        setState({ uploadProgress: successCopy });
        return res.data;
      } catch (err) {
        const errorCopy = { ...state.uploadProgress };
        errorCopy[file.name] = { state: "error", percentage: 0 };
        setState({ uploadProgress: errorCopy });
        throw err;
      }
    },
    [state.uploadProgress],
  );

  const uploadFiles =
    useCallback(async (): Promise<ProfileImageUrls | void> => {
      setState({
        uploadProgress: null,
        uploading: true,
      });
      const imageOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      try {
        if (!state.file) throw new Error("No file selected");

        const compressedFile = await imageCompression(state.file, imageOptions);
        const result = await sendRequest(compressedFile);

        const { success, data } = result as unknown as {
          success: boolean;
          data: ProfileImageUrls;
        };

        if (!success) throw new Error("Failed to upload image");

        setState({
          successfulUploaded: true,
          uploading: false,
          uploadingMessage: null,
          uploaded: data,
        });

        return data;
      } catch {
        enqueueSnackbar({
          text: t("IMG_COMPRESS_FAIL"),
          isError: true,
        });
        setState({
          successfulUploaded: false,
          uploadingMessage: t("IMG_COMPRESS_FAIL"),
          uploading: false,
          uploaded: {
            url: null,
            thumbnailUrl: null,
          },
        });
      }
    }, [state.file, sendRequest, enqueueSnackbar, t]);

  return {
    state,
    setFile,
    uploadFiles,
  };
};
